import moment from 'moment';
import * as swal from '@/helpers/swal';
import * as dateFormat from '@/helpers/dateFormat';
import { escapeHtml } from '@/helpers/freeze';
import { CFMCoreEnumsEnumLeaveShiftConflictSource, CFMDTOLeaveShiftConflictView } from '@/scripts/services/ClientApiAuto';

/**
 * Izin ile vardiyanin ayni gune denk gelmesi (is ogesi 1950): izin ve vardiya giris ekranlari
 * kaydetmeden once sorar.
 *
 * Cakisma bir ENGEL degil uyaridir: denk gelen kayitlar listelenir, kullanici onaylarsa kayit
 * yapilir. Sunucu girilen kaydin karsi tarafindaki mevcut kayitlari dondurur - izin girilirken
 * vardiyalar, vardiya girilirken izin gunleri. Cakisma gun bazindadir.
 */

/** Sunucudaki EnumLeaveShiftConflictSource ile ayni degerler: hangi kayit giriliyor. */
export const LeaveShiftConflictSource = {
    Leave: 1,
    Shift: 2,
} as const;

const DAY_FORMAT = 'DD.MM.YYYY';
const TIME_FORMAT = 'HH:mm';
const POST_DAY_FORMAT = 'YYYY-MM-DD';

/** Sunucudan gelen tarihler duvar saati; bkz. dateFormat.parseWallClock. */
const wallClock = (value: string | null | undefined, format: string) =>
    moment(<Date>dateFormat.parseWallClock(value)).format(format);

/** "12.10.2026 · Gunduz 08:00 - 16:00" ya da "12.10.2026 · Yillik Izin (8 sa)"; duz metin. */
const formatConflict = (vm: any, row: CFMDTOLeaveShiftConflictView) => {
    const day = wallClock(row.Date, DAY_FORMAT);
    if (row.ShiftId) {
        return `${day} · ${row.ShiftTypeName || ''} ${wallClock(row.ShiftStartDate, TIME_FORMAT)} - ${wallClock(row.ShiftEndDate, TIME_FORMAT)}`;
    }
    // Sure girilmemis (0) izin tam gun sayilir; saat yazilmaz. Cok gunluk izinde sure gunlere
    // bolunerek saklandigi icin kesirli olabilir.
    const hours = row.LeaveHours
        ? ` (${vm.translate('leave_shift_conflict_hours').replace('{0}', String(Math.round(row.LeaveHours * 10) / 10))})`
        : '';
    return `${day} · ${row.ExcuseTypeName || ''}${hours}`;
};

export interface LeaveShiftConflictQuery {
    userId: number;
    startDate: Date;
    endDate: Date;
    source: CFMCoreEnumsEnumLeaveShiftConflictSource;
}

/**
 * Cakisan kayitlar; cevap alinamazsa null. Uyari kaydin kendisinden onemli degil: hata yutulur ve
 * cagiran sormadan devam eder.
 */
export async function fetchLeaveShiftConflicts(vm: any, query: LeaveShiftConflictQuery)
    : Promise<CFMDTOLeaveShiftConflictView[] | null> {
    try {
        const response = await vm.$globalApiClient.shift.shiftGetLeaveConflicts({
            userId: query.userId,
            startDate: moment(query.startDate).format(POST_DAY_FORMAT),
            endDate: moment(query.endDate).format(POST_DAY_FORMAT),
            source: query.source,
        });
        return response.data.IsSuccess ? response.data.Data || [] : null;
    } catch (e) {
        console.warn('Izin-vardiya cakismasi alinamadi', e);
        return null;
    }
}

/**
 * Kaydedilecek izin ya da vardiyanin karsi taraftaki kayitlarla ayni gune denk gelip gelmedigini
 * sorar. Denk gelen yoksa ya da istek basarisizsa sormadan devam eder; varsa listeleyip onay ister.
 */
export async function confirmLeaveShiftConflicts(vm: any, query: LeaveShiftConflictQuery): Promise<boolean> {
    const conflicts = await fetchLeaveShiftConflicts(vm, query);
    if (!conflicts || conflicts.length === 0) {
        return true;
    }
    const message = query.source === LeaveShiftConflictSource.Leave
        ? 'leave_shift_conflict_leave_message'
        : 'leave_shift_conflict_shift_message';
    const html = `${vm.translate(message)}<br/>`
        + conflicts.map(row => escapeHtml(formatConflict(vm, row))).join('<br/>')
        + `<br/><br/>${vm.translate('leave_shift_conflict_question')}`;
    const answer = await swal.checkHtml(vm, vm.translate('leave_shift_conflict_title'), html);
    return !!answer.value;
}
